import {
  ChangeDetectorRef,
  Component,
  Injector,
  OnInit,
  ViewContainerRef,
  OnDestroy,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ManageGraphComponent } from '../../../insights-data/manage-graph.component';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntil, take, Subject, debounceTime } from 'rxjs';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { ApiResponses, CaseDetailModel } from '../../../../insights.interface';
import {
  GraphList,
  GraphFilterEvent,
  BehaviorGraphDataResponse,
  frequencyDatasheetGraph,
  replacementDataType,
  latencyGraph,
  partialIntervalGraph,
  frequencyIntervalGraph,
} from '../../../insights-data/insights-data.interface';
import { InsightsDataService } from '../../../insights-data/insights-data.service';
import { reportDrawerParams, ReportRes } from '../report-manager.interface';
import { InsightMode } from '../../../../insights.enums';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormControlComponent,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { SvgIconComponent } from 'angular-svg-icon';
import { CommonModule } from '@angular/common';
import { format, parse } from 'date-fns';
import _ from 'lodash';
import { ReportManagerService } from '../report-manager.service';
import { UserDatePipe } from '../../../../../../shared/pipes/user-date.pipe';
import { GraphFilterComponent } from '../../../insights-data/data-graph/graph-filter/graph-filter.component';
import { DATE_FORMAT } from '../../../feedback/feeback.enum';

@Component({
  selector: 'app-report-form',
  standalone: true,
  imports: [
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzTableModule,
    NzSelectModule,
    NzDatePickerModule,
    ReactiveFormsModule,
    NzInputModule,
    NzCheckboxModule,
    SvgIconComponent,
    CommonModule,
    ReactiveFormsModule,
    UserDatePipe,
    GraphFilterComponent,
  ],
  templateUrl: './report-form.component.html',
  styleUrl: './report-form.component.scss',
})
export class ReportFormComponent
  extends ManageGraphComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Output() formValueChange = new EventEmitter<{
    formValue: ReportRes;
    isFormValid: boolean;
  }>();
  @Input() formData: ReportRes | null | undefined = null;
  reportForm!: FormGroup;
  caseParams: reportDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.PRINT_CASE,
  };
  reportData: ReportRes | null | undefined = null;
  caseDetail: CaseDetailModel = {} as CaseDetailModel;
  private readonly destroy$ = new Subject<void>();
  checkedGraphList: GraphList[] = [];
  graphList: GraphList[] = [];
  datasheetData: { id: number; value: string }[] = [
    { id: 1, value: 'Frequency Based Datasheet' },
    { id: 2, value: 'Latency' },
    { id: 3, value: 'Partial Interval' },
    { id: 4, value: 'Replacement Behavior' },
  ];

  private activeRequests = 0;

  constructor(
    private loaderService: LoaderService,
    private drawerService: DrawerService,
    private fb: FormBuilder,
    private dataService: InsightsDataService,
    private commonService: CommonService,
    private viewContainerRef: ViewContainerRef,
    private reportService: ReportManagerService,
    private injector: Injector,
    cd: ChangeDetectorRef
  ) {
    super(cd);
    this.setupDrawerSubscription();
  }

  ngOnInit(): void {
    this.reportForm = this.fb.group({
      id: [0],
      reportName: ['', Validators.required],
      showIntervalFrequency: [false, Validators.required],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      comment: [''],
      viewType: [1, Validators.required],
      isTrendLineEnabled: [false, Validators.required],
      progressReportGraphMappings: [],
      selectedTimeOption: [null],
    });
    this.getGraphs();
    setTimeout(() =>
      this.formValueChange.emit({
        formValue: this.reportForm.value,
        isFormValid: this.reportForm.valid,
      })
    );

    this.reportForm.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(value => {
        value.graphList = this.checkedGraphList;
        this.formValueChange.emit({
          formValue: value,
          isFormValid: this.reportForm.valid,
        });
      });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<reportDrawerParams>(DRAWER_IDS.CREATE_REPORT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.caseDetail = params.case;
          this.reportData = params.selectedReport;
        }
      });
  }

  validateDates(ctrl1: string, ctrl2: string) {
    const startDate = this.reportForm.get(ctrl1)?.value;
    const endDate = this.reportForm.get(ctrl2)?.value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        this.reportForm.get(ctrl1)?.setErrors({ dateInvalid: true });
        this.reportForm.get(ctrl2)?.setErrors({ dateInvalid: true });
      } else {
        this.reportForm.get(ctrl1)?.setErrors(null);
        this.reportForm.get(ctrl2)?.setErrors(null);
      }
    }
    this.checkedGraphList = [];
  }

  isGraphChecked(data: GraphList): boolean {
    return this.checkedGraphList.some(graph => graph.id === data.id);
  }

  async patchFormValue() {
    try {
      if (this.formData) {
        const graphList = this.formData.graphList ?? [];
        const formDataForPatch = {
          ...this.formData,
          startDate: this.formData.startDate
            ? new Date(this.formData.startDate)
            : null,
          endDate: this.formData.endDate
            ? new Date(this.formData.endDate)
            : null,
          isTrendLineEnabled: this.formData.isTrendLineEnabled ?? false,
        };

        this.reportForm.patchValue(formDataForPatch, { emitEvent: false });
        this.checkedGraphList = _.uniqBy(
          _.filter(this.graphList, graph =>
            _.some(graphList, { id: graph.id })
          ),
          'id'
        );
        this.formValueChange.emit({
          formValue: this.reportForm.value,
          isFormValid: this.reportForm.valid,
        });
        if (this.checkedGraphList.length > 0) {
          for (let i = 0; i < this.checkedGraphList.length; i++) {
            this.loaderService.show();
            await this.onGraphCheckboxChange(
              true,
              this.checkedGraphList[i],
              true,
              i === this.checkedGraphList.length - 1
            );
          }
        } else {
          this.loaderService.hide();
        }
      } else {
        this.loaderService.hide();
      }
    } catch (error) {
      console.error('Error in patchFormValue:', error);
      this.loaderService.hide();
    }
  }

  onFilterChange(event: GraphFilterEvent) {
    this.reportForm.patchValue(
      {
        viewType: event.viewType,
        isTrendLineEnabled: event.isTrendLineEnabled,
        startDate: parse(event.startDate, DATE_FORMAT, new Date()),
        endDate: parse(event.endDate, DATE_FORMAT, new Date()),
        selectedTimeOption: event.selectedTimeOption || null,
      },
      { emitEvent: false }
    );
    this.checkedGraphList = [];
    this.graphList.forEach(graph => {
      graph.graphImage = [];
      graph.graphImageTotal = [];
      graph.graphData = undefined;
      graph.commentsData = [];
    });
    if (this.formData) this.formData.graphList = [];
    this.formValueChange.emit({
      formValue: {
        ...this.reportForm.value,
        graphList: this.checkedGraphList,
      } as ReportRes,
      isFormValid: this.reportForm.valid,
    });
  }

  getGraphs() {
    this.loaderService.show();
    this.activeRequests++;

    this.dataService
      .getGraphList(this.caseDetail.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponses<GraphList>) => {
          this.activeRequests--;

          if (res.result) {
            this.graphList = res.result;
            this.cd.detectChanges();

            this.handleFormDataChange();
          }

          if (this.activeRequests === 0) {
            this.loaderService.hide();
          }
        },
        error: (error: Error) => {
          this.activeRequests--;
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  async processMappedArray() {
    try {
      if (!this.formData?.progressReportGraphMappings) {
        await this.patchFormValue();
        return;
      }

      const formDataForPatch = {
        ...this.formData,
        startDate: this.formData.startDate
          ? new Date(this.formData.startDate)
          : null,
        endDate: this.formData.endDate ? new Date(this.formData.endDate) : null,
        isTrendLineEnabled: this.formData.isTrendLineEnabled ?? false,
      };

      this.reportForm.patchValue(formDataForPatch, { emitEvent: true });

      const mappedArray = _.chain(this.graphList)
        .filter(graph =>
          _.some(this.formData?.progressReportGraphMappings, {
            activityDataUnitId: graph.id,
          })
        )
        .map(graph => ({
          ...graph,
          comment: _.get(
            _.find(this.formData?.progressReportGraphMappings, {
              activityDataUnitId: graph.id,
            }),
            'comment',
            ''
          ),
        }))
        .value();
      if (mappedArray.length > 0) {
        for (let i = 0; i < mappedArray.length; i++) {
          this.loaderService.show();
          await this.onGraphCheckboxChange(
            true,
            mappedArray[i],
            true,
            i === mappedArray.length - 1
          );
        }
      } else {
        this.loaderService.hide();
      }
    } catch (error) {
      console.error('Error in processMappedArray:', error);
      this.loaderService.hide();
    }
  }

  onChange() {
    this.checkedGraphList = [];
    if (this.formData) this.formData.graphList = [];
  }

  onGraphCheckboxChange(
    event: boolean,
    data: GraphList,
    isLoop = false,
    isLastInLoop = false
  ) {
    if (event) {
      if (!this.checkedGraphList.some(graph => graph.id === data.id)) {
        this.checkedGraphList.push(data);
      }
      const isShowFrequencyInterval = this.reportForm.get(
        'showIntervalFrequency'
      )?.value;
      const selectedUnit = this.datasheetData.find(
        d => d.value === data.unitName
      );
      if (isShowFrequencyInterval && selectedUnit?.id === 1) {
        this.getGraphData(data, isLoop, isLastInLoop, 2);
      }
      return this.getGraphData(data, isLoop, isLastInLoop, 1);
    } else {
      const index = this.checkedGraphList.findIndex(
        item => item.id === data.id
      );
      this.checkedGraphList.splice(index, 1);
      this.loaderService.hide();
      return Promise.resolve();
    }
  }

  getGraphData(
    data: GraphList,
    isLoop = false,
    isLastInLoop = false,
    graphType = 1
  ): Promise<void> {
    return new Promise<void>(resolve => {
      this.isGraphDataAvailable = false;
      const selectedUnit = this.datasheetData.find(
        d => d.value === data.unitName
      );

      if (!data || !selectedUnit) {
        if (!isLoop || isLastInLoop) {
          this.loaderService.hide();
        }
        resolve();
        return;
      }

      const startDate = this.reportForm.get('startDate')?.value;
      const endDate = this.reportForm.get('endDate')?.value;

      if (!startDate || !endDate) {
        if (!isLoop || isLastInLoop) {
          this.loaderService.hide();
        }
        this.commonService.notify('error', 'Please select start and end dates');
        resolve();
        return;
      }

      this.loaderService.show();
      this.activeRequests++;
      this.dataService
        .getBehaviorGraphData({
          CaseBehaviorId: data.caseBehaviorId,
          UnitName: selectedUnit?.id,
          StartDate: format(
            this.reportForm.get('startDate')?.value,
            DATE_FORMAT
          ),
          EndDate: format(this.reportForm.get('endDate')?.value, DATE_FORMAT),
          graphType: graphType,
          viewType: this.reportForm.get('viewType')?.value,
        })
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.activeRequests--;
            if (res.result && res.result) {
              try {
                this.manageBehvaiorGraphResponseData(
                  res.result,
                  selectedUnit.id,
                  data.id,
                  data,
                  graphType
                );
              } finally {
                if (!isLoop || isLastInLoop) {
                  this.loaderService.hide();
                }
                resolve();
              }
            } else {
              data.graphImage = [];
              data.graphImageTotal = [];
              data.graphData = undefined;
              this.displayNoData = true;
              this.cd.detectChanges();

              if (!isLoop || isLastInLoop) {
                this.loaderService.hide();
              }
              resolve();
            }
            this.cd.detectChanges();
          },
          error: (error: ErrorResponse) => {
            this.activeRequests--;
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
            resolve();
          },
        });
    });
  }

  async manageBehvaiorGraphResponseData(
    data: BehaviorGraphDataResponse,
    selectedUnit: number,
    selectedGraph: number,
    graphData: GraphList,
    graphType: number
  ) {
    try {
      const success = await this.processBehaviorGraphData(
        data,
        selectedUnit,
        graphType,
        this.reportForm.get('viewType')?.value,
        this.reportForm.get('isTrendLineEnabled')?.value || false,
        async (unit, graphDataResult, showInterval) => {
          this.checkedGraphList = await this.generateImage(
            unit,
            selectedGraph,
            graphDataResult,
            showInterval,
            data.caseBehaviorId,
            data.unitName,
            data
          );
        }
      );
      if (!success) {
        graphData.graphImage = [];
        graphData.graphImageTotal = [];
        graphData.commentsData = [];
        graphData.graphData = undefined;
      }
    } catch (error) {
      console.error('Error in manageGraphResponseData:', error);
      throw error;
    }
  }

  async generateImage(
    selectedUnit: number,
    selectedGraph: number,
    data:
      | frequencyDatasheetGraph
      | latencyGraph
      | partialIntervalGraph
      | replacementDataType
      | frequencyIntervalGraph,
    showIntervalFrequency = false,
    caseBehaviorId = 0,
    unitName = '',
    behaviorData: BehaviorGraphDataResponse
  ) {
    return await this.reportService.generateGraphImage(
      this.viewContainerRef,
      selectedUnit,
      selectedGraph,
      this.checkedGraphList,
      data,
      showIntervalFrequency,
      unitName,
      caseBehaviorId,
      behaviorData
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formData'] && changes['formData'].currentValue) {
      if (this.reportForm && this.graphList.length > 0) {
        this.handleFormDataChange();
      }
    }
  }

  private async handleFormDataChange(): Promise<void> {
    if (!this.formData) return;
    const progressReportGraphMappings =
      this.formData.progressReportGraphMappings;

    if ((progressReportGraphMappings?.length ?? 0) > 0) {
      await this.processMappedArray();
    } else {
      await this.patchFormValue();
    }

    this.reportForm.patchValue(
      {
        isTrendLineEnabled: this.formData.isTrendLineEnabled ?? false,
      },
      { emitEvent: false }
    );
  }

  ngOnDestroy(): void {
    this.loaderService.hide();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
